import { useMemo } from 'react';
import { format } from 'date-fns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation, useLocations, useTableSort } from '@hooks';
import { analyticsService } from '@services';
import { formatNumber, formatPercentNumber } from '@helpers/formatNumbers';
import ExportCSV from '@features/Analytics/ExportCSV';

const SORT_COLUMNS = {
    location: 'internalName',
    views: 'views',
    engagements: 'engagements',
    calls: 'calls',
    directions: 'directions',
    website: 'website',
    conversion: 'conversion',
};

const DEFAULT_SORT = { column: 'location', dir: 'asc' };

const TableCellText = styled(Typography)({
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
});

const getTableHeader = t => {
    return [
        {
            key: 0,
            sortKey: 'location',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.location')}
                </Typography>
            ),
        },
        {
            key: 1,
            sortKey: 'views',
            align: 'right',
            component: (
                <Typography variant="subtitle2" align="right">
                    {t('AnalyticsPresence.views')}
                </Typography>
            ),
        },
        {
            key: 2,
            sortKey: 'engagements',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsSocialPosts.engagements')}
                </Typography>
            ),
        },
        {
            key: 3,
            sortKey: 'calls',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsPresence.calls')}
                </Typography>
            ),
        },
        {
            key: 4,
            sortKey: 'directions',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsPresence.directions')}
                </Typography>
            ),
        },
        {
            key: 5,
            sortKey: 'website',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsPresence.websiteClicks')}
                </Typography>
            ),
        },
        {
            key: 6,
            sortKey: 'conversion',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsConversion.conversion')}
                </Typography>
            ),
        },
    ];
};

const getTableArrow = field => {
    switch (field) {
        case 1:
            return <ArrowUpwardIcon sx={{ color: 'var(--success-main)', fontSize: '20px' }} />;
        case -1:
            return <ArrowDownwardIcon sx={{ color: 'var(--error-main)', fontSize: '20px' }} />;
        case 0:
            return null;
        default:
            return null;
    }
};

const getTableBody = data => {
    return data.map(item => {
        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {item.internalName}
                    </Typography>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <span style={{ marginRight: item.viewsChange === 0 ? '28px' : '8px' }}>
                            {formatNumber(item.views)}
                        </span>
                        {getTableArrow(item.viewsChange)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <span style={{ marginRight: item.engagementsChange === 0 ? '28px' : '8px' }}>
                            {formatNumber(item.engagements)}
                        </span>
                        {getTableArrow(item.engagementsChange)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <span style={{ marginRight: item.callsChange === 0 ? '28px' : '8px' }}>
                            {formatNumber(item.calls)}
                        </span>
                        {getTableArrow(item.callsChange)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <span style={{ marginRight: item.directionsChange === 0 ? '28px' : '8px' }}>
                            {formatNumber(item.directions)}
                        </span>
                        {getTableArrow(item.directionsChange)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <span style={{ marginRight: item.websiteChange === 0 ? '28px' : '8px' }}>
                            {formatNumber(item.website)}
                        </span>
                        {getTableArrow(item.websiteChange)}
                    </TableCellText>
                ),
            },
            {
                component: (
                    <TableCellText variant="body2" align="right">
                        <span style={{ marginRight: item.conversionChange === 0 ? '28px' : '8px' }}>
                            {formatPercentNumber(item.conversion)}
                        </span>
                        {getTableArrow(item.conversionChange)}
                    </TableCellText>
                ),
            },
        ];
    });
};

const PresenceTable = ({ isLoading, data, filter }) => {
    const { locationsMap } = useLocations();
    const { t } = useTranslation();

    const dataWithLocationNames = useMemo(
        () =>
            data.map(item => ({
                ...item,
                internalName: locationsMap[item.locationId]?.internalName || '',
            })),
        [data, locationsMap],
    );

    const { sort, onSortChange, sortedData } = useTableSort(dataWithLocationNames, {
        columns: SORT_COLUMNS,
        defaultSort: DEFAULT_SORT,
    });

    const onExport = exportFormat =>
        analyticsService.exportAnalyticsCSV(
            {
                ...filter,
                locations: filter.locations.map(item => item.id),
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            },
            'presence',
            null,
            exportFormat,
        );
    return (
        <Paper
            elevation={1}
            sx={{
                padding: '24px',
                width: '100%',
                borderRadius: '8px',
                '@media print': {
                    breakInside: 'avoid',
                },
            }}
        >
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`Header.analyticsPresence`)}
                </Typography>
                <ExportCSV type="presence" apiCall={onExport} />
            </div>
            <TableContainer>
                <Table
                    data={sortedData}
                    columnNames={getTableHeader(t)}
                    rows={getTableBody(sortedData)}
                    isDataLoading={isLoading}
                    sort={sort}
                    onSortChange={onSortChange}
                />
            </TableContainer>
        </Paper>
    );
};

export default PresenceTable;
